import type { CellEntry, GetMediaUnitsAvailabilityResponse } from '@ooh/contracts';
import { buildEntryOwnerHint } from '../../../availability/components/availability-cell-visual';
import { cellEntryToCellState } from '../../../availability/lib/cell-entry-state';
import type { CellState, EntriesGridData } from '../../../availability/lib/availability-types';
import {
  INVENTORY_OCCUPANCY_STATE_VALUES,
  type InventoryOccupancyByRowId,
  type InventoryOccupancyState,
} from '../../inventory/components/inventory-availability-shared';

/**
 * Every `CellState` that also exists as an `InventoryOccupancyState` — excludes 'free'. Built
 * from `INVENTORY_OCCUPANCY_STATE_VALUES` (the same literal tuple `InventoryOccupancyState` is
 * typed from), so a future variant added to the type can never be silently omitted here.
 */
const INVENTORY_OCCUPANCY_STATES: ReadonlySet<CellState> = new Set<InventoryOccupancyState>(
  INVENTORY_OCCUPANCY_STATE_VALUES,
);

/**
 * Maps canonical CellEntry status+ownership to InventoryOccupancyState by deriving it from
 * `cellEntryToCellState` — the single canonical status+ownership
 * mapping every `AvailabilityGrid`-based surface uses, instead of a second hand-written
 * status switch that must be kept in sync by hand. FREE has no occupancy range, so it maps
 * to `null` (no range added). This keeps own held cells SELECT-eligible
 * (`isShiftSelectableState`) on every surface that consumes InventoryOccupancyByRowId
 * (inventory catalog, single-unit DetailAvailabilityCard), enabling multi-select Zwolnij
 * for cells the caller holds — see findings.md "Inventory catalog collapses own
 * reservations to 'reserved-other'".
 */
function canonicalEntryToOccupancyState(entry: CellEntry): InventoryOccupancyState | null {
  const state = cellEntryToCellState(entry);
  return INVENTORY_OCCUPANCY_STATES.has(state) ? (state as InventoryOccupancyState) : null;
}

/**
 * Converts GET /media-units/availability response to InventoryOccupancyByRowId.
 *
 * Each unit becomes a map entry keyed by String(mediaUnitId). Each period with
 * a non-FREE dominant entry contributes one occupancy range (the dominant entry
 * by status precedence — highest status index wins).
 */
export function mapAvailabilityResponseToOccupancy(
  response: GetMediaUnitsAvailabilityResponse,
): InventoryOccupancyByRowId {
  const result: InventoryOccupancyByRowId = {};

  if (!Array.isArray(response.units)) return result;

  for (const unit of response.units) {
    const rowId = String(unit.mediaUnitId);
    const ranges: InventoryOccupancyByRowId[string] = [];

    for (const period of unit.periods) {
      // Pick dominant entry: first in entries[] (API sorts by precedence desc — highest first, index 0 is most dominant)
      const dominant = period.entries[0];
      if (!dominant) continue;

      const state = canonicalEntryToOccupancyState(dominant);
      if (state === null) continue;

      const ownerHint = buildEntryOwnerHint(dominant);

      ranges.push({
        start: period.start,
        end: period.end,
        state,
        ownerHint,
        offerTitle: dominant.offerTitle,
        conditional: dominant.conditional === true,
        // The reservation's OWN status / deadline — distinct from start/end above (this
        // range's calendar-period boundaries). Undefined/null for a base-state or
        // availability-window dominant entry, which has no reservation.
        reservationStatus: dominant.status,
        ttlExpiresAt: dominant.ttlExpiresAt ?? null,
      });
    }

    result[rowId] = ranges;
  }

  return result;
}

/**
 * Maps GET /media-units/availability response to EntriesGridData.
 *
 * EntriesGridData: unitId (string) → periodId → CellEntry[]
 *
 * The inventory view uses month-granularity period IDs ('YYYY-MM'). The API
 * returns raw ISO date pairs (start/end). A period matches when the API range
 * overlaps the display month. We use the exact period.start YYYY-MM prefix to
 * derive the period ID so it matches buildPeriods('month') output.
 *
 * This is a best-effort mapping for multi-stack rendering: each API period is
 * mapped to the matching inventory period ID (YYYY-MM prefix of period.start).
 */
function accumulatePeriodEntries(
  periodMap: Record<string, CellEntry[]>,
  periodId: string,
  entries: CellEntry[],
): void {
  if (entries.length === 0) return;
  const existing = periodMap[periodId];
  if (existing !== undefined) {
    existing.push(...entries);
  } else {
    periodMap[periodId] = [...entries];
  }
}

export function mapAvailabilityResponseToEntriesGrid(
  response: GetMediaUnitsAvailabilityResponse,
): EntriesGridData {
  const result: EntriesGridData = {};

  if (!Array.isArray(response.units)) return result;

  for (const unit of response.units) {
    const rowId = String(unit.mediaUnitId);
    const periodMap: Record<string, CellEntry[]> = {};

    for (const period of unit.periods) {
      // Derive inventory period ID from the start date YYYY-MM prefix.
      // This matches buildPeriods('month') output (month-granularity IDs).
      const periodId = period.start.slice(0, 7);
      accumulatePeriodEntries(periodMap, periodId, period.entries);
    }

    result[rowId] = periodMap;
  }

  return result;
}
